/**
 * Calculator API (RTK Query)
 *
 * Network contracts for the Profit Calculator. All calculator MATH is client-side
 * (the ported @domain engines); only product lookup, recent restore, workspace
 * persistence and the marketplaces reference list go over the network.
 *
 * aragorn calls `/profit/<path>` on the legolas origin via the authenticated
 * apiClient (see apiClientBaseQuery). legolas rewrites to sauron `/api/v1/<path>`
 * and injects identity — so NO `user_id` is ever sent from the client.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClientBaseQuery } from '@/store/api/apiClientBaseQuery';

// ── Wire shapes (snake_case — mirror sauron Pydantic schemas) ──────────────────

export interface ProductContextRaw {
    asin: string;
    marketplace: string;
    product_name?: string | null;
    brand_name?: string | null;
    category?: string | null;
    fulfillment_type?: string | null;
    bsr?: number | null;
    bsr_display?: string | null;
    size_tier?: string | null;
    upc?: string | null;
    model_number?: string | null;
    dimensions_mm?: {
        length?: number | null;
        width?: number | null;
        height?: number | null;
    } | null;
    weight_g?: number | null;
    current_price?: number | null;
    estimated_cogs?: number | null;
    estimated_monthly_units_sold?: number | null;
    primary_image_url?: string | null;
    currency_code?: string | null;
}

export interface FeeBasisRaw {
    referral_fee_rate: number;
    fba_fixed_fee: number;
    returns_rate: number;
    returns_recovery_rate: number;
    referral_fee_known: boolean;
    fba_fee_known: boolean;
    returns_rate_known: boolean;
    dimensions_known: boolean;
    fees_derived_from_dimensions: boolean;
}

export interface ProductLookupResponse {
    product_context: ProductContextRaw;
    fee_basis: FeeBasisRaw;
}

export interface MarketplaceRaw {
    code: string; // "US", "UK", … — value used in lookup / WorkspaceKey
    currency: string; // "USD", "GBP", …
    keepa_domain: string; // "US", "GB", …
}

export interface MarketplacesResponse {
    marketplaces: MarketplaceRaw[];
}

/** Workspace identity. NOTE: never includes user_id — legolas owns identity. */
export interface WorkspaceKey {
    marketplace: string;
    asin: string;
    business_model: string;
}

export interface WorkspaceLoadResponse {
    found: boolean;
    workspace: Record<string, unknown> | null;
    state_version: number;
}

export interface WorkspaceSaveResponse {
    saved: boolean;
    state_version: number;
    updated_at: string;
}

export interface WorkspaceResetResponse {
    reset: boolean;
    workspace: Record<string, unknown>;
    state_version: number;
}

// ── API ─────────────────────────────────────────────────────────────────────

export const calculatorApi = createApi({
    reducerPath: 'calculatorApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['Workspace'],
    endpoints: builder => ({
        // ASIN lookup ("Calculate") — POST /profit/products/lookup
        lookupProduct: builder.mutation<
            ProductLookupResponse,
            { asin: string; marketplace: string }
        >({
            query: body => ({ url: 'profit/products/lookup', method: 'POST', body }),
        }),

        // Restore last product — GET /profit/products/recent
        getRecentProduct: builder.query<ProductLookupResponse | null, void>({
            query: () => ({ url: 'profit/products/recent' }),
        }),

        // Marketplaces reference list — GET /profit/marketplaces
        getMarketplaces: builder.query<MarketplacesResponse, void>({
            query: () => ({ url: 'profit/marketplaces' }),
            keepUnusedDataFor: 3600,
        }),

        // Load saved workspace — GET /profit/profit-dashboard/workspace/state
        loadWorkspace: builder.query<WorkspaceLoadResponse, WorkspaceKey>({
            query: ({ marketplace, asin, business_model }) => ({
                url: 'profit/profit-dashboard/workspace/state',
                params: { marketplace, asin, business_model },
            }),
            providesTags: (_res, _err, arg) => [{ type: 'Workspace', id: `${arg.marketplace}:${arg.asin}` }],
        }),

        // Autosave workspace — POST /profit/profit-dashboard/workspace/state/save
        saveWorkspace: builder.mutation<
            WorkspaceSaveResponse,
            { key: WorkspaceKey; workspace: Record<string, unknown> }
        >({
            query: body => ({
                url: 'profit/profit-dashboard/workspace/state/save',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, arg) => [
                { type: 'Workspace', id: `${arg.key.marketplace}:${arg.key.asin}` },
            ],
        }),

        // Reset workspace — POST /profit/profit-dashboard/workspace/state/reset
        resetWorkspace: builder.mutation<WorkspaceResetResponse, { key: WorkspaceKey }>({
            query: body => ({
                url: 'profit/profit-dashboard/workspace/state/reset',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, arg) => [
                { type: 'Workspace', id: `${arg.key.marketplace}:${arg.key.asin}` },
            ],
        }),
    }),
});

export const {
    useLookupProductMutation,
    useGetRecentProductQuery,
    useLazyGetRecentProductQuery,
    useGetMarketplacesQuery,
    useLoadWorkspaceQuery,
    useLazyLoadWorkspaceQuery,
    useSaveWorkspaceMutation,
    useResetWorkspaceMutation,
} = calculatorApi;
